// plan-05 S06: UI shell — panel, input, buttons, save pattern

import type { HexGrid } from './geometry'
import { build } from './geometry'
import { computeMaturity } from './maturity'
import { createHeatmapSVG, resetColors, type HeatmapSVG } from './render'
import { animateHeatmap, createAnimationController, type AnimationController } from './animate'
import { createPaintState, applyPaintVisuals, clearPaint, cyclePaint, type PaintState } from './paint'
import { decode, buildVocab } from './decode'
import { play } from '../../audio/sfx'
import { load, set, bump } from '../../core/save'
import { SLOT_X, SLOT_Y, SLOT_Z, SLOT_A } from '../../data/slotPool'
import { exportCard, downloadBlob, safeWord } from '../../core/exportCard'
import { strHash } from './prng'

const BASE32_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
function shortHash4(s: string): string {
  let bits = strHash(s) >>> 0
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += BASE32_CHARS[bits & 0x1F]
    bits = bits >>> 5
  }
  return out
}

export interface HeatmapPanel {
  el: HTMLElement
  grid: HexGrid
  groups: HeatmapSVG
  animCtrl: AnimationController
  paintState: PaintState
  lastResult: ReturnType<typeof computeMaturity> | null
  lastHits: number[] | null
  setLastPaintKey(key: string | null): void
  destroy(): void
}

export function createPanel(): HeatmapPanel {
  const grid = build()
  const groups = createHeatmapSVG(grid)
  const animCtrl = createAnimationController()
  const paintState = createPaintState(grid.total)

  // panel container
  const panel = document.createElement('div')
  panel.className = 'vt-heatmap-panel'
  panel.setAttribute('role', 'region')
  panel.setAttribute('aria-label', 'consensus heatmap')

  // label
  const label = document.createElement('div')
  label.className = 'vt-heatmap-label'
  label.textContent = 'HEATMAP.EXE // PA TRAFFIC'
  panel.appendChild(label)

  // input row
  const inputRow = document.createElement('div')
  inputRow.className = 'vt-heatmap-input-row'

  const input = document.createElement('input')
  input.type = 'text'
  input.placeholder = 'enter word...'
  input.maxLength = 64
  input.autocomplete = 'off'
  input.spellcheck = false
  input.className = 'vt-heatmap-input'
  inputRow.appendChild(input)

  const runBtn = mkBtn('[ run ]')
  inputRow.appendChild(runBtn)

  const decodeBtn = mkBtn('[ decode ]')
  inputRow.appendChild(decodeBtn)

  const animBtn = mkBtn('[ * ]')
  animBtn.title = 'animations on — click to disable'
  inputRow.appendChild(animBtn)

  const paintBtn = mkBtn('[ paint ]')
  inputRow.appendChild(paintBtn)

  const saveBtn = mkBtn('[ save ]')
  saveBtn.style.display = 'none'
  inputRow.appendChild(saveBtn)

  const exportBtn = mkBtn('[ export ]')
  inputRow.appendChild(exportBtn)

  const clearBtn = mkBtn('[ clear ]')
  inputRow.appendChild(clearBtn)

  const closeBtn = mkBtn('[ x ]')
  inputRow.appendChild(closeBtn)

  panel.appendChild(inputRow)

  // status line
  const statusLine = document.createElement('div')
  statusLine.className = 'vt-heatmap-status'
  statusLine.style.display = 'none'
  panel.appendChild(statusLine)

  // decode result line
  const decodeResult = document.createElement('div')
  decodeResult.className = 'vt-heatmap-decode'
  decodeResult.style.display = 'none'
  panel.appendChild(decodeResult)

  // paint label
  const paintLabel = document.createElement('div')
  paintLabel.className = 'vt-heatmap-paint-label'
  paintLabel.style.display = 'none'
  paintLabel.textContent = 'click hexes to set maturity'
  panel.appendChild(paintLabel)

  // SVG
  groups.svg.style.cursor = 'default'
  panel.appendChild(groups.svg)

  // mount to parent (stage body or legacy panels area)
  const mount = document.getElementById('vt-stage-body') ?? document.getElementById('vt-panels-mount')
  if (mount) mount.appendChild(panel)

  // state
  let lastResult: ReturnType<typeof computeMaturity> | null = null
  let lastHits: number[] | null = null
  let lastWord: string | null = null
  let lastPaintKey: string | null = null

  // --- event handlers ---

  function runWord(word: string): void {
    if (!word) {
      resetColors(grid, groups)
      lastResult = null
      lastHits = null
      return
    }

    paintState.paintMode = false
    paintBtn.textContent = '[ paint ]'
    paintLabel.style.display = 'none'
    saveBtn.style.display = 'none'

    const result = computeMaturity(grid, word)
    lastResult = result
    lastHits = result.hits.slice()
    lastWord = word
    lastPaintKey = null
    updateExportEnabled()

    animateHeatmap(grid, result, groups, animCtrl)

    // first-seen logic
    const s = load()
    const lower = word.toLowerCase()
    const idx = s.consensusSearched.indexOf(lower)
    if (idx === -1) {
      // first observation
      const searched = [...s.consensusSearched, lower]
      set('consensusSearched', searched)
      statusLine.style.display = 'block'
      statusLine.textContent = '> first observation logged. probably.'
      play('quorum_chime')
    } else {
      const count = s.consensusSearched.filter(w => w === lower).length + 1
      statusLine.style.display = 'block'
      statusLine.textContent = `> observation #${count}. drift: probably.`
    }
    bump('events')
  }

  runBtn.addEventListener('click', () => {
    runWord(input.value.trim())
  })

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runWord(input.value.trim())
  })

  decodeBtn.addEventListener('click', async () => {
    const target = paintState.paintMode ? paintState.paintHits.slice() : lastHits
    if (!target || target.every(v => v === 0)) {
      decodeResult.style.display = 'block'
      decodeResult.textContent = paintState.paintMode
        ? 'paint some hexes first, then decode'
        : 'run a word first, then decode'
      return
    }

    decodeResult.style.display = 'block'
    decodeResult.textContent = 'scanning...'

    const vocab = buildVocab(SLOT_X, SLOT_Y, SLOT_Z, SLOT_A)
    const result = await decode(target, grid, vocab, (pct) => {
      decodeResult.textContent = `scanning... ${pct}%`
    })

    const pct = Math.round(result.score * 100)
    decodeResult.textContent = `≈ "${result.word}" (${pct}% match)`
    play('quorum_chime')
  })

  // animation toggle
  animBtn.addEventListener('click', () => {
    animCtrl.enabled = !animCtrl.enabled
    animBtn.textContent = animCtrl.enabled ? '[ * ]' : '[ - ]'
    animBtn.title = animCtrl.enabled
      ? 'animations on — click to disable'
      : 'animations off — click to enable'
  })

  // paint toggle
  paintBtn.addEventListener('click', () => {
    paintState.paintMode = !paintState.paintMode
    if (paintState.paintMode) {
      paintBtn.textContent = '[ painting ]'
      paintLabel.style.display = 'block'
      paintLabel.textContent = `${paintState.paintedCount} hex${paintState.paintedCount !== 1 ? 'es' : ''} painted -- click to cycle`
      saveBtn.style.display = ''
      applyPaintVisuals(grid, paintState)
      // clear lines in paint mode
      resetColors(grid, groups)
      applyPaintVisuals(grid, paintState)
      animCtrl.cancel()
    } else {
      paintBtn.textContent = '[ paint ]'
      paintLabel.style.display = 'none'
      saveBtn.style.display = 'none'
      // restore last run if available
      if (lastResult) {
        animateHeatmap(grid, lastResult, groups, animCtrl)
      }
    }
    updateExportEnabled()
  })

  // save pattern
  saveBtn.addEventListener('click', () => {
    if (paintState.paintedCount === 0) return
    const s = load()
    // auto-generate 4-char key
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
    let key = ''
    for (let i = 0; i < 4; i++) key += chars[Math.floor(Math.random() * chars.length)]
    // ensure unique
    while (s.paintedAt[key]) {
      key = ''
      for (let i = 0; i < 4; i++) key += chars[Math.floor(Math.random() * chars.length)]
    }
    const newPainted = { ...s.paintedAt, [key]: paintState.paintHits.slice() }
    set('paintedAt', newPainted)
    paintLabel.textContent = `saved as ${key}. recall: paint ${key}`
    lastPaintKey = key
    updateExportEnabled()
    play('command_accept')
  })

  // plan-19 S05: export PNG card
  function updateExportEnabled(): void {
    const enabled =
      (lastHits !== null && lastHits.some(v => v > 0)) ||
      (paintState.paintMode && paintState.paintedCount > 0)
    if (enabled) {
      exportBtn.style.opacity = ''
      exportBtn.style.cursor = ''
      exportBtn.style.pointerEvents = ''
    } else {
      exportBtn.style.opacity = '0.4'
      exportBtn.style.cursor = 'not-allowed'
      exportBtn.style.pointerEvents = 'none'
    }
  }

  function showExportSubscript(text: string, kind: 'ok' | 'err' = 'ok'): void {
    statusLine.style.display = 'block'
    statusLine.classList.toggle('vt-heatmap-status-export', kind === 'ok')
    statusLine.style.color = kind === 'ok' ? 'var(--vt-amber)' : 'var(--vt-emergency-red)'
    statusLine.textContent = text
    statusLine.style.opacity = '1'
    setTimeout(() => {
      statusLine.style.opacity = '0'
      setTimeout(() => {
        if (statusLine.textContent === text) {
          statusLine.style.display = 'none'
          statusLine.style.opacity = '1'
          statusLine.classList.remove('vt-heatmap-status-export')
          statusLine.style.color = 'var(--vt-green-dim)'
        }
      }, 600)
    }, 4800)
  }

  exportBtn.addEventListener('click', async () => {
    const useWord = lastWord && lastHits && lastHits.some(v => v > 0)
    const usePaint = paintState.paintMode && paintState.paintedCount > 0
    if (!useWord && !usePaint) return

    play('command_accept')
    const originalLabel = exportBtn.textContent
    exportBtn.textContent = '[ rendering... ]'
    exportBtn.style.pointerEvents = 'none'

    try {
      let spec
      let filename: string
      if (usePaint && lastPaintKey) {
        filename = `vpk-paint-${lastPaintKey}.png`
        spec = {
          title: 'HEATMAP.EXE // CONSENSUS GRID',
          headerLine: `> paint: ${lastPaintKey}`,
          content: { kind: 'svg' as const, svg: groups.svg },
          stampUrl: `vaguepostking.com/?paint=${lastPaintKey}`,
          stampHash: `@${lastPaintKey}`,
          filename,
        }
      } else if (usePaint && !lastPaintKey) {
        // painted but not saved — use ephemeral pattern hash
        const ephemeralKey = shortHash4(paintState.paintHits.join(','))
        filename = `vpk-paint-${ephemeralKey}.png`
        spec = {
          title: 'HEATMAP.EXE // CONSENSUS GRID',
          headerLine: `> paint: ${ephemeralKey}`,
          content: { kind: 'svg' as const, svg: groups.svg },
          stampUrl: `vaguepostking.com/#heatmap=`,
          stampHash: `@${ephemeralKey}`,
          filename,
        }
      } else {
        const word = lastWord as string
        const sw = safeWord(word)
        const sh = shortHash4(word)
        filename = sw ? `vpk-consensus-${sw}-${sh}.png` : `vpk-consensus-${sh}.png`
        spec = {
          title: 'HEATMAP.EXE // CONSENSUS GRID',
          headerLine: `> consensus: "${word}"`,
          content: { kind: 'svg' as const, svg: groups.svg },
          stampUrl: `vaguepostking.com/#heatmap=${word}`,
          stampHash: `@${sh}`,
          filename,
        }
      }

      const blob = await exportCard(spec)
      downloadBlob(blob, filename)

      play('quorum_chime')
      const s = load()
      set('cardExports', { ...s.cardExports, heatmap: s.cardExports.heatmap + 1 })
      bump('events')

      showExportSubscript(`> card exported. ${filename}. probably.`)
    } catch (err) {
      play('command_reject')
      const errLine = document.createElement('div')
      errLine.className = 'vt-line-oracle'
      errLine.style.color = 'var(--vt-green-dim)'
      errLine.textContent = '> export failed. consensus: probably.'
      const history = document.querySelector('.vt-history')
      if (history) history.appendChild(errLine)
      void err
      showExportSubscript('> export failed. consensus: probably.', 'err')
    } finally {
      exportBtn.textContent = originalLabel
      exportBtn.style.pointerEvents = ''
    }
  })

  // clear
  clearBtn.addEventListener('click', () => {
    clearPaint(paintState, grid.total)
    if (paintState.paintMode) {
      applyPaintVisuals(grid, paintState)
      paintLabel.textContent = 'click hexes to set maturity'
    }
    decodeResult.style.display = 'none'
    statusLine.style.display = 'none'
    updateExportEnabled()
  })

  // close
  closeBtn.addEventListener('click', () => {
    panel.style.display = 'none'
    animCtrl.cancel()
  })

  // hex click for paint mode
  for (const h of grid.ordered) {
    const poly = h.poly!
    const parent = poly.parentElement!
    parent.addEventListener('click', () => {
      if (!paintState.paintMode) return
      const now = cyclePaint(paintState, h.seqNum)

      requestAnimationFrame(() => {
        const { fill, stroke } = getPaintColor(now)
        poly.setAttribute('fill', fill)
        poly.setAttribute('stroke', stroke)
        paintLabel.textContent = `${paintState.paintedCount} hex${paintState.paintedCount !== 1 ? 'es' : ''} painted -- click to cycle`
        updateExportEnabled()
      })
    })
  }

  // initial disabled state
  updateExportEnabled()

  // recall saved paint pattern via URL
  const savedPaintKey = new URLSearchParams(window.location.search).get('paint')
  if (savedPaintKey) {
    const s = load()
    const pattern = s.paintedAt[savedPaintKey]
    if (pattern && pattern.length === grid.total) {
      paintState.paintMode = true
      paintState.paintHits = pattern.slice()
      paintState.paintedCount = pattern.filter(v => v > 0).length
      paintBtn.textContent = '[ painting ]'
      paintLabel.style.display = 'block'
      paintLabel.textContent = `${paintState.paintedCount} hex${paintState.paintedCount !== 1 ? 'es' : ''} painted -- click to cycle`
      saveBtn.style.display = ''
      lastPaintKey = savedPaintKey
      applyPaintVisuals(grid, paintState)
      updateExportEnabled()
    }
  }

  return {
    el: panel,
    grid,
    groups,
    animCtrl,
    paintState,
    get lastResult() { return lastResult },
    get lastHits() { return lastHits },
    setLastPaintKey(key: string | null) {
      lastPaintKey = key
      updateExportEnabled()
    },
    destroy() {
      animCtrl.cancel()
      panel.remove()
    },
  }
}

function mkBtn(text: string): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.className = 'vt-heatmap-btn'
  btn.textContent = text
  btn.setAttribute('role', 'button')
  return btn
}

function getPaintColor(v: number): { fill: string; stroke: string } {
  if (v === 0) return { fill: 'rgba(0, 90, 0, 0.10)', stroke: 'rgba(0, 90, 0, 0.28)' }
  const colors = [
    { fill: 'rgba(0, 180, 0, 0.50)', stroke: 'rgba(0, 180, 0, 0.85)' },
    { fill: 'rgba(0, 210, 0, 0.65)', stroke: 'rgba(0, 210, 0, 0.95)' },
    { fill: 'rgba(0, 210, 0, 0.85)', stroke: 'rgba(0, 210, 0, 0.95)' },
    { fill: 'rgba(0, 176, 255, 0.72)', stroke: 'rgba(0, 176, 255, 0.95)' },
    { fill: 'rgba(0, 176, 255, 0.92)', stroke: 'rgba(0, 176, 255, 0.95)' },
    { fill: 'rgba(40, 180, 220, 0.95)', stroke: 'rgba(40, 180, 220, 0.95)' },
  ]
  return colors[Math.min(v - 1, colors.length - 1)]
}
